import { useState } from "react";
import { toast } from "sonner";
import api, { formatAxiosError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Shield, Bot, Copy, Check } from "lucide-react";

/** Mon compte (partenaire) : changement de mot de passe — miroir de la carte « Sécurité » de Settings.js (staff), même
 * endpoint PUT /settings/password — et connexion de Claude (même connecteur MCP que l'équipe, outils partenaire). */
export default function PartnerAccount() {
  const { brand } = useAuth();
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  // URL de l'API (émetteur OAuth du connecteur), pas celle de la page : un partenaire peut être sur le domaine de la société.
  const mcpUrl = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/mcp`;
  const company = brand?.name || "la société";

  const copyUrl = () =>
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(mcpUrl))
      .then(() => {
        setCopied(true);
        toast.success("URL du connecteur copiée");
        setTimeout(() => setCopied(false), 1600);
      })
      .catch(() => toast.error("Copie impossible : sélectionnez l'URL à la main"));

  const changePassword = async () => {
    if (!pwForm.current || !pwForm.next) return toast.error("Remplissez tous les champs");
    if (pwForm.next !== pwForm.confirm) return toast.error("Les mots de passe ne correspondent pas");
    if (pwForm.next.length < 8) return toast.error("Minimum 8 caractères");
    setSaving(true);
    try {
      await api.put("/settings/password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Mot de passe mis à jour");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatAxiosError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-md" data-testid="partner-account-page">
      <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Portail partenaire</p>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-6">Mon compte</h1>

      <div className="bg-card border border-border rounded-sm p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Sécurité</h3>
        </div>
        <div className="space-y-3">
          <div>
            <Label htmlFor="partner-account-password-current">Mot de passe actuel</Label>
            <Input type="password" value={pwForm.current} onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))}
              id="partner-account-password-current" autoComplete="current-password" data-testid="partner-account-password-current" className="mt-1.5 rounded-sm" />
          </div>
          <div>
            <Label htmlFor="partner-account-password-next">Nouveau mot de passe</Label>
            <Input type="password" value={pwForm.next} onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))}
              id="partner-account-password-next" autoComplete="new-password" data-testid="partner-account-password-next" className="mt-1.5 rounded-sm" />
          </div>
          <div>
            <Label htmlFor="partner-account-password-confirm">Confirmer le nouveau mot de passe</Label>
            <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
              id="partner-account-password-confirm" autoComplete="new-password" data-testid="partner-account-password-confirm" className="mt-1.5 rounded-sm" />
          </div>
          <Button onClick={changePassword} disabled={saving} data-testid="partner-account-password-submit" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold w-full">
            {saving ? "Mise à jour…" : "Changer le mot de passe"}
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-6 space-y-4 mt-6" data-testid="partner-account-claude">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Connecter Claude</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Préparez, envoyez et suivez vos offres en langage naturel depuis Claude (web, ordinateur ou mobile).
        </p>
        <div className="flex items-stretch gap-2">
          <code className="flex-1 min-w-0 bg-muted rounded-sm px-3 py-2 flex items-center font-mono text-xs break-all" data-testid="partner-mcp-url">{mcpUrl}</code>
          <Button type="button" variant="outline" onClick={copyUrl} data-testid="partner-mcp-copy" aria-label="Copier l'URL du connecteur" className="rounded-sm h-11 shrink-0">
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
        <ol className="list-decimal pl-5 space-y-2 text-sm text-foreground/90">
          <li>Dans Claude : <strong>Settings → Connectors → Add custom connector</strong>, collez l'URL ci-dessus.</li>
          <li>Connectez-vous avec votre compte partenaire, choisissez <strong>{company} — espace partenaire</strong>, puis <strong>Autoriser</strong>.</li>
          <li>Demandez par exemple : <em>« Prépare une offre fibre 12 mois pour Marie Dupont, particulier, marie@exemple.fr »</em>.</li>
        </ol>
        <p className="text-xs text-muted-foreground">
          Claude n'accède qu'au catalogue, à vos clients et à vos offres ; les prix sont toujours calculés par {company}.
        </p>
      </div>
    </div>
  );
}
